import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { safeStorage } from '@/utils/safeStorage';

const STORAGE_KEY = 'mw_calendar_views';
const MAX_FREE_VIEWS = 5;

export const useNewsletterGate = () => {
  const { user, isSubscribed } = useAuth();
  const [viewCount, setViewCount] = useState(0);
  const [isNewsletterSubscribed, setIsNewsletterSubscribed] = useState(false);
  const [showGate, setShowGate] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Check newsletter subscription status
  useEffect(() => {
    if (typeof window === 'undefined') {
      setIsLoading(false);
      return;
    }
    const checkSubscription = () => {
      const subscribed = safeStorage.getItem('mw_newsletter_subscribed') === 'true';
      setIsNewsletterSubscribed(subscribed);

      const storedViews = parseInt(safeStorage.getItem(STORAGE_KEY) || '0', 10);
      setViewCount(storedViews);
      setIsLoading(false);
    };

    checkSubscription();
  }, []);

  // Increment view count
  const incrementViewCount = useCallback(() => {
    // Premium users and newsletter subscribers bypass the gate
    if (isSubscribed || isNewsletterSubscribed) {
      return;
    }

    const newCount = viewCount + 1;
    setViewCount(newCount);
    safeStorage.setItem(STORAGE_KEY, newCount.toString());

    // Show gate after exceeding max free views
    if (newCount > MAX_FREE_VIEWS) {
      setShowGate(true);
    }
  }, [viewCount, isSubscribed, isNewsletterSubscribed]);

  // Mark as subscribed to newsletter
  const markAsSubscribed = useCallback(() => {
    safeStorage.setItem('mw_newsletter_subscribed', 'true');
    setIsNewsletterSubscribed(true);
    setShowGate(false);
  }, []);

  // Close the gate WITHOUT resetting the view count — dismissing must not grant another
  // 5 free views (ERI-1624). The user stays over the cap and is re-gated on the next view.
  const closeGate = useCallback(() => {
    setShowGate(false);
  }, []);

  // Check if gate should be shown
  const shouldShowGate = !isLoading && !isSubscribed && !isNewsletterSubscribed && viewCount > MAX_FREE_VIEWS;

  return {
    viewCount,
    maxFreeViews: MAX_FREE_VIEWS,
    isNewsletterSubscribed,
    showGate: showGate && shouldShowGate,
    shouldShowGate,
    isLoading,
    incrementViewCount,
    markAsSubscribed,
    closeGate,
    remainingViews: Math.max(0, MAX_FREE_VIEWS - viewCount),
  };
};
